import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  ExpressCheckoutElement,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { motion, AnimatePresence } from 'framer-motion';
import { STRIPE_PUBLISHABLE_KEY, createPaymentIntent } from '../api';
import { COLOR, FONT } from '../theme';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const APPEARANCE = {
  theme: 'night',
  variables: {
    colorPrimary: COLOR.accent,
    colorBackground: '#1c1c1f',
    colorText: '#f4f4f5',
    colorTextSecondary: '#71717a',
    colorDanger: '#f87171',
    fontFamily: FONT,
    fontSizeBase: '15px',
    borderRadius: '14px',
    spacingUnit: '5px',
    colorIconTab: '#71717a',
    colorIconTabSelected: '#ffffff',
    colorIconTabHover: '#d4d4d8',
  },
  rules: {
    '.Input': {
      backgroundColor: '#27272a',
      border: '1px solid rgba(255,255,255,0.09)',
      boxShadow: 'none',
      padding: '13px 16px',
      fontSize: '15px',
      color: '#f4f4f5',
      letterSpacing: '0.1px',
    },
    '.Input:focus': {
      border: '1px solid rgba(255,255,255,0.38)',
      boxShadow: '0 0 0 3px rgba(255,255,255,0.06)',
    },
    '.Input--invalid': {
      border: '1px solid rgba(248,113,113,0.55)',
    },
    '.Label': {
      color: '#71717a',
      fontWeight: '500',
      fontSize: '11px',
      letterSpacing: '0.7px',
      textTransform: 'uppercase',
      marginBottom: '7px',
    },
    '.Tab': {
      backgroundColor: '#27272a',
      border: '1px solid rgba(255,255,255,0.08)',
      color: '#71717a',
      borderRadius: '12px',
      padding: '10px 14px',
    },
    '.Tab--selected': {
      backgroundColor: '#3f3f46',
      border: '1px solid rgba(255,255,255,0.26)',
      color: '#f4f4f5',
    },
    '.Tab:hover': {
      backgroundColor: '#323236',
      color: '#d4d4d8',
      border: '1px solid rgba(255,255,255,0.13)',
    },
    '.Tab:focus': { boxShadow: 'none', outline: 'none' },
    '.Tab--selected:focus': { boxShadow: 'none' },
    '.TabIcon--selected': { fill: '#f4f4f5' },
    '.TabLabel--selected': { color: '#f4f4f5', fontWeight: '600' },
    '.AccordionItem': {
      backgroundColor: '#1c1c1f',
      borderRadius: '16px',
      border: '1px solid rgba(255,255,255,0.08)',
      marginBottom: '10px',
      overflow: 'hidden',
    },
    '.AccordionItem--selected': {
      border: '1px solid rgba(255,255,255,0.24)',
      backgroundColor: '#202023',
    },
    '.Block': {
      backgroundColor: '#27272a',
      border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: '12px',
    },
    '.CheckboxInput': {
      backgroundColor: '#27272a',
      border: '1px solid rgba(255,255,255,0.18)',
      borderRadius: '6px',
    },
    '.CheckboxInput--checked': {
      backgroundColor: '#ffffff',
      borderColor: '#ffffff',
    },
    '.TermsText': { color: '#52525b', fontSize: '11px' },
    '.Error': { color: '#f87171', fontSize: '13px' },
    '.PickerItem': {
      backgroundColor: '#27272a',
      border: '1px solid rgba(255,255,255,0.08)',
      borderRadius: '10px',
    },
    '.PickerItem--selected': {
      border: '1px solid rgba(255,255,255,0.28)',
      backgroundColor: '#3f3f46',
    },
  },
};

// ─── Inner payment sheet ───────────────────────────────────────────────────────
function PaymentSheet({ total, roomNumber, guestInfo, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [walletReady, setWalletReady] = useState(false);
  const [hasWallets, setHasWallets] = useState(false);

  const doConfirm = async () => {
    const { paymentIntent, error: err } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}${window.location.pathname}`,
      },
      redirect: 'if_required',
    });
    if (err) {
      setError(err.message);
      setLoading(false);
    } else if (paymentIntent?.status === 'succeeded') {
      onSuccess(paymentIntent.id);
    } else {
      setError('Payment could not be completed. Please try again.');
      setLoading(false);
    }
  };

  // The wallet hands us the payer's email in the confirm event (because
  // `emailRequired` is set). Stripe also records it on the charge's
  // billing_details, and createOrder reads it there server-side — so this is the
  // fast path, not the only path, and losing it costs nothing.
  const onExpressConfirm = async (event) => {
    if (!stripe || !elements) return;
    setLoading(true);
    setError('');
    const walletEmail = event && event.billingDetails && event.billingDetails.email;
    if (walletEmail) guestInfo.email = guestInfo.email || walletEmail;
    await doConfirm();
  };

  const onCardSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setError('');
    const { error: subErr } = await elements.submit();
    if (subErr) {
      setError(subErr.message);
      setLoading(false);
      return;
    }
    await doConfirm();
  };

  const initial = (guestInfo.name || 'G')[0].toUpperCase();

  return (
    <div style={s.overlay} onClick={onCancel}>
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 36, stiffness: 360 }}
        style={s.sheet}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Handle */}
        <div style={s.handle} />

        {/* ── Summary header ── */}
        <div style={s.summaryHeader}>
          <div style={s.summaryLeft}>
            <p style={s.summaryRoom}>Room {roomNumber}</p>
            <div style={s.summaryAmountRow}>
              <span style={s.summaryAUD}>A$</span>
              <span style={s.summaryAmount}>{total.toFixed(2)}</span>
            </div>
          </div>
          <div style={s.guestPill}>
            <div style={s.guestAvatar}>{initial}</div>
            <span style={s.guestPillName}>{guestInfo.name?.split(' ')[0] || 'Guest'}</span>
            <span style={s.verifiedDot} />
          </div>
        </div>

        {/* ── Scrollable payment body ── */}
        <form onSubmit={onCardSubmit} style={s.form}>
          <div style={s.scroll}>

            {/* Express checkout: Apple Pay / Google Pay. White buttons read best on the
               dark sheet. The section only reserves space until we know whether a wallet
               is available, then collapses if there's none (no empty gap). */}
            <AnimatePresence>
              {walletReady && hasWallets && (
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={s.expressLabel}>
                  Express checkout
                </motion.p>
              )}
            </AnimatePresence>
            <div style={{
              ...s.expressSection,
              minHeight: walletReady && !hasWallets ? 0 : 52,
              marginBottom: walletReady && !hasWallets ? 0 : 4,
            }}>
              <ExpressCheckoutElement
                onReady={({ availablePaymentMethods }) => {
                  const any = availablePaymentMethods
                    ? Object.values(availablePaymentMethods).some(Boolean)
                    : false;
                  setHasWallets(any);
                  setWalletReady(true);
                }}
                onConfirm={onExpressConfirm}
                options={{
                  buttonType: { applePay: 'buy', googlePay: 'buy' },
                  buttonTheme: { applePay: 'white', googlePay: 'white' },
                  buttonHeight: 50,
                  layout: { maxColumns: 1, maxRows: 2 },
                  // Ask the wallet for the payer's email. This is how a guest gets
                  // their confirmation and receipt WITHOUT typing anything — they
                  // already authenticate with Face ID, and the address comes with
                  // it. Without this flag Apple and Google send no email at all
                  // and the guest is unreachable.
                  emailRequired: true,
                }}
              />
            </div>

            {/* Divider — only after wallets confirmed present */}
            <AnimatePresence>
              {walletReady && hasWallets && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  style={s.divider}
                >
                  <div style={s.divLine} />
                  <span style={s.divText}>or pay another way</span>
                  <div style={s.divLine} />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Payment methods: Klarna · AfterPay · Card */}
            <AnimatePresence>
              {(!walletReady || walletReady) && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <PaymentElement
                    options={{
                      layout: { type: 'accordion', defaultCollapsed: false, radios: 'always', spacedAccordionItems: true },
                      wallets: { applePay: 'never', googlePay: 'never' },
                      terms: { card: 'never', klarna: 'never', afterpayClearpay: 'never' },
                      defaultValues: {
                        billingDetails: {
                          name: guestInfo.name || '',
                          email: guestInfo.email || '',
                        },
                      },
                      fields: {
                        billingDetails: { name: 'never', email: 'never' },
                      },
                    }}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Error */}
            <AnimatePresence>
              {error && (
                <motion.div
                  key="error"
                  initial={{ opacity: 0, y: -8, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  style={s.errorBox}
                >
                  <span style={s.errorIcon}>⚠️</span>
                  <span>{error}</span>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Security note */}
            <div style={s.secureRow}>
              <span style={s.lockIcon}>🔒</span>
              <span style={s.secureText}>Encrypted & secured by Stripe</span>
              <span style={s.stripeWordmark}>stripe</span>
            </div>

            <div style={{ height: 8 }} />
          </div>

          {/* ── Sticky footer ── */}
          <div style={s.footer}>
            <motion.button
              type="submit"
              whileTap={{ scale: 0.98 }}
              disabled={!stripe || loading}
              style={{
                ...s.payBtn,
                opacity: loading ? 0.75 : 1,
                cursor: loading ? 'default' : 'pointer',
              }}
            >
              {loading
                ? <PayBtnSpinner />
                : (
                  <span style={s.payBtnInner}>
                    <span>Pay A${total.toFixed(2)}</span>
                    <span style={s.payBtnArrow}>→</span>
                  </span>
                )
              }
            </motion.button>
            <button type="button" style={s.cancelBtn} onClick={onCancel}>
              Cancel
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function PayBtnSpinner() {
  return (
    <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
      <motion.span
        style={{ width: 18, height: 18, borderRadius: '50%', border: '2.5px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', display: 'block', flexShrink: 0 }}
        animate={{ rotate: 360 }}
        transition={{ repeat: Infinity, ease: 'linear', duration: 0.72 }}
      />
      Processing…
    </span>
  );
}

// ─── Outer wrapper: fetches clientSecret, then mounts Elements ─────────────────
function StripePaymentForm({ total, roomNumber, restaurantId, guestInfo, items, notes, onSuccess, onCancel }) {
  const [clientSecret, setClientSecret] = useState('');
  const [fetchError, setFetchError] = useState('');

  useEffect(() => {
    createPaymentIntent({
      amount: total,
      hotelId: guestInfo.hotelId,
      roomNumber,
      restaurantId,
      customerEmail: guestInfo.email,
      items,
      guestName: guestInfo.name,
      guestPhone: guestInfo.phone,
      notes,
    })
      .then((data) => {
        if (data.clientSecret) setClientSecret(data.clientSecret);
        else setFetchError('Could not start payment. Please try again.');
      })
      .catch(() => setFetchError('Network error. Please check your connection and try again.'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (fetchError) {
    return (
      <div style={s.overlay}>
        <motion.div
          initial={{ y: '100%' }} animate={{ y: 0 }}
          transition={{ type: 'spring', damping: 36, stiffness: 360 }}
          style={s.sheet}
        >
          <div style={s.handle} />
          <div style={s.errorState}>
            <div style={s.errorEmoji}>⚠️</div>
            <p style={s.errorStateTitle}>Payment unavailable</p>
            <p style={s.errorStateSub}>{fetchError}</p>
            <button style={s.errorStateBtn} onClick={onCancel}>Go back</button>
          </div>
        </motion.div>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div style={s.overlay}>
        <motion.div
          initial={{ y: '100%' }} animate={{ y: 0 }}
          transition={{ type: 'spring', damping: 36, stiffness: 360 }}
          style={s.sheet}
        >
          <div style={s.handle} />
          <div style={s.summaryHeader}>
            <div style={s.summaryLeft}>
              <p style={s.summaryRoom}>Room {roomNumber}</p>
              <div style={s.summaryAmountRow}>
                <span style={s.summaryAUD}>A$</span>
                <span style={s.summaryAmount}>{total.toFixed(2)}</span>
              </div>
            </div>
          </div>
          <div style={s.loadingBody}>
            <motion.div
              style={s.loadingRing}
              animate={{ rotate: 360 }}
              transition={{ repeat: Infinity, ease: 'linear', duration: 0.9 }}
            />
            <p style={s.loadingText}>Preparing secure payment…</p>
          </div>
          <div style={s.footer}>
            <button style={s.cancelBtn} onClick={onCancel}>Cancel</button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance: APPEARANCE }}>
      <PaymentSheet
        total={total}
        roomNumber={roomNumber}
        guestInfo={guestInfo}
        onSuccess={onSuccess}
        onCancel={onCancel}
      />
    </Elements>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.65)',
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)',
    zIndex: 400,
    display: 'flex', alignItems: 'flex-end',
  },
  sheet: {
    background: '#0f0f11',
    borderRadius: '28px 28px 0 0',
    width: '100%', maxWidth: '430px', margin: '0 auto',
    maxHeight: '93vh',
    display: 'flex', flexDirection: 'column',
    fontFamily: FONT,
    overflow: 'hidden',
  },
  handle: {
    width: '36px', height: '4px',
    background: 'rgba(255,255,255,0.16)',
    borderRadius: '2px',
    margin: '10px auto 0',
    flexShrink: 0,
  },

  // Summary header
  summaryHeader: {
    display: 'flex', justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '20px 22px 18px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    flexShrink: 0,
  },
  summaryLeft: {},
  summaryRoom: {
    fontSize: 12, color: '#71717a',
    margin: '0 0 4px',
    letterSpacing: '0.3px', fontWeight: '500',
    textTransform: 'uppercase',
  },
  summaryAmountRow: {
    display: 'flex', alignItems: 'baseline', gap: 3,
  },
  summaryAUD: {
    fontSize: 18, fontWeight: '600', color: '#a1a1aa',
    letterSpacing: '-0.3px',
  },
  summaryAmount: {
    fontSize: 40, fontWeight: '700',
    color: '#ffffff',
    letterSpacing: '-2px', lineHeight: 1,
  },
  guestPill: {
    display: 'flex', alignItems: 'center', gap: 7,
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 30, padding: '7px 14px 7px 7px',
    marginTop: 6,
  },
  guestAvatar: {
    width: 28, height: 28, borderRadius: '50%',
    background: COLOR.accent, color: '#fff',
    fontSize: 12, fontWeight: '700',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  guestPillName: {
    fontSize: 13, fontWeight: '600', color: '#d4d4d8',
  },
  verifiedDot: {
    width: 7, height: 7, borderRadius: '50%',
    background: '#22c55e',
    boxShadow: '0 0 5px rgba(34,197,94,0.7)',
  },

  // Form layout
  form: {
    display: 'flex', flexDirection: 'column',
    flex: 1, overflow: 'hidden', minHeight: 0,
  },
  scroll: {
    flex: 1, overflowY: 'auto', overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none', msOverflowStyle: 'none',
    padding: '20px 20px 0',
  },

  // Express checkout
  expressLabel: {
    fontSize: 11, fontWeight: '600', color: '#71717a',
    letterSpacing: '0.5px', textTransform: 'uppercase',
    margin: '0 0 10px',
  },
  expressSection: {
    minHeight: 52,
    marginBottom: 4,
  },

  // Divider
  divider: {
    display: 'flex', alignItems: 'center', gap: 10,
    margin: '16px 0',
  },
  divLine: {
    flex: 1, height: 1,
    background: 'rgba(255,255,255,0.06)',
  },
  divText: {
    fontSize: 11, color: '#52525b',
    whiteSpace: 'nowrap', letterSpacing: '0.3px',
  },

  // Error
  errorBox: {
    display: 'flex', alignItems: 'flex-start', gap: 8,
    background: 'rgba(248,113,113,0.08)',
    border: '1px solid rgba(248,113,113,0.18)',
    borderRadius: 14, padding: '13px 15px',
    fontSize: 13, color: '#f87171',
    margin: '12px 0',
    lineHeight: 1.5,
    overflow: 'hidden',
  },
  errorIcon: { flexShrink: 0, marginTop: 1 },

  // Security row
  secureRow: {
    display: 'flex', alignItems: 'center', gap: 6,
    justifyContent: 'center',
    margin: '16px 0 0',
    padding: '10px 0',
    borderTop: '1px solid rgba(255,255,255,0.04)',
  },
  lockIcon: { fontSize: 11 },
  secureText: { fontSize: 11, color: '#3f3f46', letterSpacing: '0.2px' },
  stripeWordmark: {
    fontSize: 11, color: '#3f3f46',
    fontStyle: 'italic', fontWeight: '700',
    letterSpacing: '0.5px',
  },

  // Footer
  footer: {
    padding: '14px 20px 42px',
    borderTop: '1px solid rgba(255,255,255,0.05)',
    background: '#0f0f11',
    flexShrink: 0,
  },
  payBtn: {
    width: '100%',
    background: `linear-gradient(135deg, ${COLOR.accent} 0%, ${COLOR.accentDeep} 100%)`, color: '#ffffff',
    border: 'none', borderRadius: 18,
    padding: '17px 24px',
    fontSize: 17, fontWeight: '700',
    fontFamily: 'inherit',
    letterSpacing: '-0.3px',
    marginBottom: 10,
    display: 'block',
    overflow: 'hidden',
  },
  payBtnInner: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    width: '100%',
  },
  payBtnArrow: {
    fontSize: 20, fontWeight: '300', opacity: 0.75,
  },
  cancelBtn: {
    width: '100%',
    background: 'transparent', color: '#52525b',
    border: 'none', padding: '10px',
    fontSize: 14, cursor: 'pointer',
    fontFamily: 'inherit', display: 'block',
  },

  // Loading state
  loadingBody: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: 14,
    padding: '52px 24px',
  },
  loadingRing: {
    width: 34, height: 34, borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.07)',
    borderTopColor: '#a1a1aa',
  },
  loadingText: { fontSize: 14, color: '#52525b', margin: 0 },

  // Error state (fetch error)
  errorState: {
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', textAlign: 'center',
    padding: '40px 28px 32px',
  },
  errorEmoji: { fontSize: 36, marginBottom: 16 },
  errorStateTitle: { fontSize: 18, fontWeight: '700', color: '#fff', margin: '0 0 8px' },
  errorStateSub: { fontSize: 14, color: '#71717a', margin: '0 0 28px', lineHeight: 1.5 },
  errorStateBtn: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 14, padding: '13px 32px',
    color: '#d4d4d8', fontSize: 15,
    cursor: 'pointer', fontFamily: 'inherit',
  },
};

export default StripePaymentForm;
